import { useMemo } from "react";
import {
  EntityGroup,
  Icon,
  useIsMobile,
  type EntityGroupItem,
} from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";

import { useAppData } from "#app/lib/data";
import { entityToGroupItem, type EntityData } from "#app/lib/entity-visuals";
import {
  splitAssigneeValue,
  UNASSIGNED_ASSIGNEE_ID,
} from "#app/lib/filters/build-where";
import type { IssueScope } from "#app/lib/filters/issue-scope";
import { scopeWorkflowId } from "#app/lib/filters/issue-scope";
import type {
  FilterConfig,
  QuickFilterDef,
  UseFiltersResult,
} from "#app/lib/filters/types";
import { FilterToggle, type FilterToggleSize } from "./filter-toggle";
import {
  useQuickFilterOptions,
  writeQuickValue,
} from "./quick-inputs/quick-filter-options";
import { QuickEntityPicker } from "#app/components/filters/quick-inputs/quick-entity-picker";
import { useAssigneeRoster } from "./use-assignee-roster";

type AssigneeFilterChipProps = Omittable<{
  config: FilterConfig;
  filters: UseFiltersResult;
  scope?: IssueScope;
  size?: FilterToggleSize;
  /** Hover tooltip, doubling as the accessible name's context. */
  tooltip?: string;
}>;

const CHIP_LABEL = "Assignee";

/** Stack slots — up to three faces; past that the last slot is the "+N"
 *  chip (the app-wide avatar-group rung). */
const MAX_FACES = 3;

/**
 * "Unassigned" as a slot in the applied stack: the empty avatar — the person
 * glyph on the neutral wash, the face a member with no name would wear — so
 * "nobody" stands in the row of faces as one of them.
 */
const UNASSIGNED_ITEM: EntityGroupItem = {
  id: UNASSIGNED_ASSIGNEE_ID,
  name: null,
  fallbackIcon: "user",
};

/**
 * The Assignee filter's own door on the toolbar: a chip beside the filter
 * chip that opens the same member pool the Simple panel's Assignee row
 * opens, and reads the same value — one filter, two doors, so the panel's
 * count and Clear all need nothing new for it. Pass the LAYER the field
 * lives in as `filters` (`quickLayerFor` on the issue screens): inside a
 * saved view that is the view's own draft, so a pick here previews at once,
 * lights Save view for whoever can save, and is what the view keeps.
 *
 * The pool leads with Unassigned, so the work nobody has is one press from
 * the toolbar; on a team screen the team's roster follows, then everyone
 * else. Applied, the chip reads its value as the value: ONE avatar stack
 * standing where the glyph stood (the me-mode chip's grammar), Unassigned
 * in it as an empty face, three slots before the "+N" chip takes the last.
 * A quiet × trails the stack and the hover-revealed clear disc paints over
 * it, so the chip both shows its off switch at rest and clears like every
 * other selected chip.
 *
 * A phone renders nothing here — its filter drawer drills into the same
 * Assignee page, and the toolbar row has no room for a second chip.
 */
export function AssigneeFilterChip({
  config,
  filters,
  scope,
  size = "default",
  tooltip = "Filter by assignee",
}: AssigneeFilterChipProps) {
  const isMobile = useIsMobile();
  const { currentUser } = useAppData();

  const def = useMemo<QuickFilterDef | undefined>(
    () => config.quickFilters?.find((d) => d.type === "entity-assignee"),
    [config.quickFilters],
  );
  const defs = useMemo(() => (def ? [def] : []), [def]);
  const roster = useAssigneeRoster(scope);
  const [section] = useQuickFilterOptions(
    defs,
    scope ? scopeWorkflowId(scope) : undefined,
    roster,
    (field) => filters.quickValues[field],
  );

  if (!def || !section || isMobile) return null;

  const value = filters.quickValues[def.field];
  const ids = Array.isArray(value) ? (value as string[]) : [];
  const { unassigned } = splitAssigneeValue(ids);
  // Through the shared write path, so an id the roster no longer holds (a
  // member removed since the view saved them) retires on the first edit.
  const setValue = (next: unknown) => writeQuickValue(filters, section, next);

  // The faces the index can still name — an id whose member is gone keeps
  // the chip lit with no face to show for it.
  const entities = section.options
    .filter((option) => option.entity && option.selected(value))
    .map((option) => option.entity)
    .filter((entity): entity is EntityData => !!entity);

  const items: EntityGroupItem[] = [
    ...(unassigned ? [UNASSIGNED_ITEM] : []),
    ...entities.map((entity) => {
      const item = entityToGroupItem(entity);
      // The viewer's own avatar renders themed to them — stacks never opt
      // into the true color (the rule every group stack follows).
      return currentUser?.userId === entity.id && item.avatarColor
        ? { ...item, avatarColor: null }
        : item;
    }),
  ];

  // Three slots, the way every avatar stack in the app folds: up to three
  // faces, and past that the last slot becomes the "+N" chip so the stack
  // never grows past its three-slot width.
  const face =
    items.length > 0 ? (
      <EntityGroup items={items} size="2xs" max={MAX_FACES} />
    ) : undefined;

  return (
    <QuickEntityPicker
      section={section}
      value={value}
      onChange={setValue}
      trigger={
        <FilterToggle
          size={size}
          icon="users"
          face={face}
          label={CHIP_LABEL}
          selected={ids.length > 0}
          // The resting × the clear disc overlays on hover: it reserves
          // the disc's slot so the stack never shifts, and says at rest
          // that the chip comes off here.
          readout={
            ids.length > 0 ? <Icon name="x" size="sm" color="muted" /> : null
          }
          onClear={() => setValue(null)}
          tooltip={tooltip}
          clearTooltip="Clear assignee filter"
        />
      }
    />
  );
}
